import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReportList } from "./reports/ReportKit";
import { ScoreRow, ScoreDetail, ratingFor } from "./reports/ScoreAnalysis";
import { Zap, ChevronRight, Wand2 } from "lucide-react";

/**
 * FitMatrix — credit-score-style skill comparison.
 * Each requirement is a rated row (traffic-light badge + one-liner) that
 * opens a full detail view. When analysisId is provided (dashboard context)
 * the biggest lever + gap details link straight into the Resume Studio.
 */
export default function FitMatrix({ matrix = [], overall = 0, verdict = "", strengths = [], gaps = [], analysisId = null }) {
  const [sel, setSel] = useState(null);
  const navigate = useNavigate();
  const row = sel != null ? matrix[sel] : null;

  const goStudio = (requirement) => {
    navigate(`/studio?analysis_id=${encodeURIComponent(analysisId)}&focus=${encodeURIComponent(requirement)}`);
  };

  let leverIdx = -1;
  if (matrix.length) {
    leverIdx = 0;
    matrix.forEach((r, i) => { if ((r.score || 0) < (matrix[leverIdx].score || 0)) leverIdx = i; });
    if ((matrix[leverIdx].score || 0) >= 60) leverIdx = -1;
  }
  const lever = leverIdx >= 0 ? matrix[leverIdx] : null;

  return (
    <div className="space-y-6" data-testid="fit-matrix">
      {/* Overall score */}
      <div className="flex items-center gap-5">
        <div
          className="w-24 h-24 rounded-2xl flex items-center justify-center font-display text-3xl"
          style={{
            background: ratingFor(overall).color,
            border: "4px solid #0F1B2D",
            boxShadow: "5px 5px 0 0 #0F1B2D",
            color: "white",
          }}
          data-testid="overall-score"
        >
          {Math.round(overall)}
        </div>
        <div className="flex-1">
          <div className="rm-label mb-1">Overall Fit · {ratingFor(overall).label}</div>
          <div className="font-display text-xl leading-tight" data-testid="fit-verdict">
            {verdict || "—"}
          </div>
        </div>
      </div>

      {/* Biggest lever — the one weak factor that moves the score most */}
      {lever && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setSel(leverIdx)}
          onKeyDown={(e) => { if (e.key === "Enter") setSel(leverIdx); }}
          className="w-full flex items-center gap-3 p-3.5 rounded-lg text-left cursor-pointer transition-transform hover:-translate-y-0.5"
          style={{ background: "#FFF2EC", border: "2px dashed #FF6A00" }}
          data-testid="fit-biggest-lever"
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
               style={{ background: "#FF6A00", color: "white", border: "2px solid #0F1B2D" }}>
            <Zap size={17} strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-rm text-[10px] uppercase tracking-widest font-bold" style={{ color: "#FF6A00" }}>
              · Biggest lever
            </div>
            <div className="font-bold text-sm leading-snug" style={{ color: "#0F1B2D" }}>{lever.requirement}</div>
            <div className="text-xs mt-0.5" style={{ color: "#64748B" }}>
              Fixing this one factor could lift your fit to ~{Math.min(100, Math.round(overall + (100 - (lever.score || 0)) / matrix.length))}
              {" "}({ratingFor(Math.min(100, Math.round(overall + (100 - (lever.score || 0)) / matrix.length))).label}). Tap to see the gap.
            </div>
          </div>
          {analysisId ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); goStudio(lever.requirement); }}
              className="rm-btn rm-btn-primary text-[10px] px-2.5 py-1.5 flex-shrink-0 inline-flex items-center gap-1"
              data-testid="fit-lever-studio-btn"
            >
              <Wand2 size={11} /> Fix in Studio
            </button>
          ) : (
            <ChevronRight size={18} className="flex-shrink-0" style={{ color: "#FF6A00" }} />
          )}
        </div>
      )}

      {/* Rated factor rows */}
      <div className="space-y-2" data-testid="fit-matrix-rows">
        {matrix.map((r, idx) => (
          <ScoreRow
            key={idx}
            score={r.score}
            title={r.requirement}
            description={r.user_evidence}
            onClick={() => setSel(idx)}
            testId={`fit-row-${idx}`}
          />
        ))}
      </div>

      {/* Strengths & Gaps */}
      {(strengths.length > 0 || gaps.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div
            className="p-4 rounded-md"
            style={{ border: "1.5px solid #A7F3D0", background: "#ECFDF5" }}
            data-testid="strengths-list"
          >
            <ReportList title="Strengths" color="#0E5A4A" items={strengths} />
          </div>
          <div
            className="p-4 rounded-md"
            style={{ border: "1.5px solid #FECACA", background: "#FEF2F2" }}
            data-testid="gaps-list"
          >
            <ReportList title="Gaps to close" color="#B91C1C" items={gaps} />
          </div>
        </div>
      )}

      <ScoreDetail
        open={row != null}
        onClose={() => setSel(null)}
        score={row ? row.score : 0}
        eyebrow="Fit factor"
        title={row ? row.requirement : ""}
        testId="fit-detail"
      >
        <p className="whitespace-pre-line">{row ? row.user_evidence : ""}</p>
        {row?.status && (
          <div className="mt-4 font-mono-rm text-[10px] uppercase tracking-widest font-bold" style={{ color: "#64748B" }}>
            Match status · {row.status}
          </div>
        )}
        {analysisId && row && (row.score || 0) < 75 && (
          <button
            type="button"
            onClick={() => goStudio(row.requirement)}
            className="rm-btn rm-btn-primary text-xs px-3 py-2 mt-5 inline-flex items-center gap-1.5"
            data-testid="fit-detail-studio-btn"
          >
            <Wand2 size={13} /> Fix it in Studio →
          </button>
        )}
      </ScoreDetail>
    </div>
  );
}
